const PRIVACY_BANNER_PATH = '/cdn/shopifycloud/privacy-banner/privacy-banner.js';
const WAIT_TIMEOUT = 15000;

const privacyBannerAPI = () =>
  typeof window.privacyBanner?.showPreferences === 'function' ? window.privacyBanner : null;

function waitFor(check, signal, { script, invoke } = {}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer;
    let interval;
    const finish = (error, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearInterval(interval);
      signal.removeEventListener('abort', onAbort);
      script?.removeEventListener('load', checkReady);
      script?.removeEventListener('error', onScriptError);
      if (error) reject(error);
      else resolve(value);
    };
    const checkReady = () => {
      try {
        const value = check();
        if (value) finish(null, value);
      } catch (error) {
        finish(error);
      }
    };
    const onAbort = () => finish(new DOMException('Aborted', 'AbortError'));
    const onScriptError = () => {
      if (script?.dataset.delectroCookiePreferences === 'loading') {
        script.dataset.delectroCookiePreferences = 'failed';
      }
      finish(new Error('Privacy banner script could not load'));
    };
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
    script?.addEventListener('load', checkReady);
    script?.addEventListener('error', onScriptError);
    timer = setTimeout(() => finish(new Error('Cookie preferences timed out')), WAIT_TIMEOUT);
    interval = setInterval(checkReady, 100);
    checkReady();
    if (settled) return;
    try {
      Promise.resolve(invoke?.()).catch((error) => finish(error));
      checkReady();
    } catch (error) {
      finish(error);
    }
  });
}

async function loadPrivacyBanner(signal) {
  const api = privacyBannerAPI();
  if (api) return api;
  let script = [...document.scripts].find((candidate) => {
    if (!candidate.src) return false;
    const url = new URL(candidate.src, document.baseURI);
    return (
      (url.origin === window.location.origin || url.hostname === 'cdn.shopify.com') &&
      url.pathname.endsWith('/shopifycloud/privacy-banner/privacy-banner.js')
    );
  });
  if (script?.dataset.delectroCookiePreferences === 'failed') {
    script.remove();
    script = undefined;
  }
  const needsScript = !script;
  if (needsScript) {
    script = document.createElement('script');
    script.src = PRIVACY_BANNER_PATH;
    script.async = true;
    script.dataset.delectroCookiePreferences = 'loading';
  }
  return waitFor(privacyBannerAPI, signal, {
    script,
    invoke: () => {
      if (needsScript) document.head.append(script);
    },
  });
}

function preferencesVisible() {
  const wrapper = document.getElementById('shopify-pc__prefs');
  const dialog = document.getElementById('shopify-pc__prefs__dialog');
  return [wrapper, dialog].every((element) => {
    if (!element || !element.getClientRects().length) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

class DelectroCookiePreferences extends HTMLElement {
  #listeners;
  #operation;
  #button;
  #status;

  connectedCallback() {
    this.#listeners?.abort();
    this.#operation?.abort();
    this.#operation = undefined;
    this.#button = this.querySelector('[data-cookie-preferences-open]');
    this.#status = this.querySelector('[data-cookie-preferences-status]');
    if (!(this.#button instanceof HTMLButtonElement) || !(this.#status instanceof HTMLElement)) return;
    this.#listeners = new AbortController();
    this.#button.disabled = false;
    this.#button.removeAttribute('aria-busy');
    this.#button.textContent = this.dataset.label;
    this.#status.hidden = true;
    this.#status.textContent = '';
    this.#button.addEventListener('click', this.#open, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#operation?.abort();
    this.#operation = undefined;
  }

  #open = async () => {
    if (this.#operation) return;
    const operation = new AbortController();
    this.#operation = operation;
    this.#button.disabled = true;
    this.#button.setAttribute('aria-busy', 'true');
    this.#button.textContent = this.dataset.loadingLabel;
    this.#status.textContent = this.dataset.loadingLabel;
    this.#status.hidden = false;
    let failed = false;
    try {
      this.#button.closest('[popover]')?.hidePopover?.();
      const trigger = this.querySelector('.policy-list-trigger');
      if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true });
      const banner = await loadPrivacyBanner(operation.signal);
      await waitFor(preferencesVisible, operation.signal, { invoke: () => banner.showPreferences() });
      if (this.#operation !== operation) return;
      this.#status.textContent = '';
      this.#status.hidden = true;
    } catch (error) {
      if (operation.signal.aborted || this.#operation !== operation) return;
      failed = true;
      this.#status.textContent = this.dataset.errorMessage;
      this.#status.hidden = false;
    } finally {
      if (this.#operation === operation) {
        this.#operation = undefined;
        this.#button.disabled = false;
        this.#button.removeAttribute('aria-busy');
        this.#button.textContent = failed ? this.dataset.retryLabel : this.dataset.label;
      }
    }
  };
}

if (!customElements.get('delectro-cookie-preferences')) {
  customElements.define('delectro-cookie-preferences', DelectroCookiePreferences);
}
